import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import moment from 'moment';

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bell,
  Calendar,
  Clock,
  FileText,
  CheckCircle2,
  X,
  DollarSign,
  MessageCircle
} from "lucide-react";

const notificationIcons = {
  schedule_assigned: Calendar,
  schedule_changed: Calendar,
  clock_in: Clock,
  clock_out: Clock,
  estimate_sent: FileText,
  estimate_accepted: DollarSign,
  estimate_rejected: FileText,
  direct_message: MessageCircle
};

export default function NotificationsDropdown() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(false);

  useEffect(() => {
    if (user) {
      shouldReconnectRef.current = true;
      fetchNotifications();
      connectWebSocket();
      
      // Fallback polling every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      
      return () => {
        shouldReconnectRef.current = false;
        clearInterval(interval);
        if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close();
        else if (wsRef.current?.readyState === WebSocket.CONNECTING) wsRef.current.onopen = () => wsRef.current?.close();
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const connectWebSocket = async () => {
    try {
      if (!shouldReconnectRef.current || [WebSocket.OPEN, WebSocket.CONNECTING].includes(wsRef.current?.readyState)) return;
      // Get session token from cookie (this is just for demo - in production use proper token management)
      const sessionToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('session_token='))
        ?.split('=')[1];
      
      if (!sessionToken) {
        return;
      }

      const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/notifications/${encodeURIComponent(sessionToken)}`;
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'unread_count') {
          setUnreadCount(data.count);
        } else if (data.type === 'notification') {
          // New notification received
          setNotifications(prev => [data.data, ...prev.slice(0, 19)]);
          setUnreadCount(prev => prev + 1);
          
          // Show browser notification if permission granted
          if (Notification.permission === 'granted') {
            new Notification(data.data.title, {
              body: data.data.body,
              icon: '/icons/icon-192x192.png'
            });
          }
        }
      };
      
      ws.onclose = () => {
        wsRef.current = null;
        if (shouldReconnectRef.current) reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };
      
      ws.onerror = (error) => {
      };
      
      wsRef.current = ws;
      
      // Send periodic pings
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);
      
      ws.addEventListener('close', () => clearInterval(pingInterval));
      
    } catch (error) {
      // Error handled silently — non-critical operation
    }
  };

  const fetchNotifications = async () => {
    try {
      const [notifResponse, countResponse] = await Promise.all([
        axios.get(`${API}/notifications?limit=20`),
        axios.get(`${API}/notifications/unread-count`)
      ]);
      // API returns array directly for notifications
      setNotifications(Array.isArray(notifResponse.data) ? notifResponse.data : []);
      setUnreadCount(countResponse.data?.count || 0);
    } catch (error) {
      // Set defaults to prevent crashes
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      // Error handled silently — non-critical operation
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      // Error handled silently — non-critical operation
    }
  };

  const handleNotificationClick = (notification) => {
    handleMarkRead(notification.notification_id);
    
    // Navigate based on notification type (API uses 'type' field)
    const notifType = notification.type || '';
    if (notifType === 'schedule_assigned' || notifType === 'schedule_changed') {
      navigate('/my-schedule');
    } else if (notifType === 'clock_in' || notifType === 'clock_out') {
      navigate('/calendar');
    } else if (notifType === 'estimate_sent' || notifType === 'estimate_accepted') {
      navigate('/estimates');
    } else if (notifType === 'customer_message' || notifType === 'customer_request') {
      navigate(`/projects/${notification.ref_id}`);
    } else if (notifType === 'direct_message') {
      navigate(`/chat?tab=direct&channel=${notification.ref_id}`);
    }
    
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map(notification => {
              const Icon = notificationIcons[notification.type] || Bell;
              return (
                <div
                  key={notification.notification_id}
                  className={`flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                    !notification.read ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    !notification.read ? 'bg-indigo-100' : 'bg-slate-100'
                  }`}>
                    <Icon className={`w-4 h-4 ${!notification.read ? 'text-indigo-600' : 'text-slate-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.read ? 'font-medium' : ''}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{notification.body}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {moment(notification.created_at).fromNow()}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-2"></div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center text-slate-500">
              <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p>No notifications</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
